import { useState } from "react";
import { Download, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { DEPTS } from "@/constants/crm";
import { exportCsv } from "@/lib/api";
import { PageHeader, StatCard, Panel, StatusBadge } from "@/components/crm/common";
import { DataTable } from "@/components/crm/DataTable";
import { FilterBar, applyFilters } from "@/components/crm/FilterBar";
import { useAuth } from "@/context/AuthContext";
import { PersonChip } from "@/components/crm/UserImage";

const rollup = (ts) => {
  if (!ts.length) return null;
  for (const s of ["Delayed", "In Progress", "Pending"]) if (ts.some((t) => t.status === s)) return s;
  return "Complete";
};

export default function Tracker() {
  const { items: tasks, update } = useCollection("tasks");
  const { items: projects, update: updateProject } = useCollection("projects");
  const canEdit = useAuth().can("Tracker", "edit");
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("");
  const [proj, setProj] = useState("");
  const open = tasks.filter((t) => t.status !== "Complete");
  const rows = applyFilters(open, search, ["deliverable", "assignee", "next_action"], [{ key: "department", value: dept }, { key: "project_id", value: proj }]);
  const active = projects.filter((p) => p.stage !== "Closure");
  const cols = [
    { key: "project_name", label: "Project" }, { key: "department", label: "Department" }, { key: "deliverable", label: "Task" },
    { key: "assignee", label: "Owner", render: (r) => <PersonChip name={r.assignee} /> }, { key: "deadline", label: "Deadline" }, { key: "dependency", label: "Dependency" },
    { key: "next_action", label: "Next action" }, { key: "status", label: "Status" },
  ];

  return (
    <>
      <PageHeader num="05" title="Central Project Tracker" subtitle="A common status view for every department.">
        <Button data-testid="tracker-export-button" variant="outline" onClick={() => exportCsv("project-tracker.csv", rows, cols)}><Download className="h-4 w-4 mr-1" />Export report</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="tracker-on-track" label="Projects on track" value={active.filter((p) => p.status === "On Track").length} tone="text-emerald-600" />
        <StatCard testid="tracker-at-risk" label="Projects at risk" value={active.filter((p) => p.status === "At Risk").length} tone="text-rose-600" />
        <StatCard testid="tracker-open-tasks" label="Open tasks" value={open.length} />
        <StatCard testid="tracker-delayed" label="Delayed tasks" value={tasks.filter((t) => t.status === "Delayed").length} tone="text-rose-600" />
      </div>
      <Panel title="Department status matrix">
        <DataTable testid="tracker-matrix" rows={active} columns={[
          { key: "name", label: "Project", render: (p) => <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{p.name}</span><StatusBadge value={p.status} /></div> },
          ...DEPTS.map((d) => ({ key: d, label: d, render: (p) => { const s = rollup(tasks.filter((t) => t.project_id === p.id && t.department === d)); return s ? <StatusBadge value={s} /> : <span className="text-slate-300">—</span>; } })),
          { key: "health", label: "", render: (p) => {
            const next = p.status === "At Risk" ? "On Track" : "At Risk";
            return canEdit && <Button data-testid={`toggle-health-${p.id}`} size="sm" variant="ghost" onClick={() => updateProject(p.id, { status: next }, `Marked ${next}`)}>Mark {next}</Button>;
          } },
        ]} />
      </Panel>
      <Panel title="Next actions, owners & deadlines">
        <FilterBar testid="tracker" search={search} setSearch={setSearch} filters={[
          { key: "department", label: "departments", value: dept, set: setDept, options: DEPTS },
          { key: "project", label: "projects", value: proj, set: setProj, options: projects.map((p) => ({ value: p.id, label: p.name })) },
        ]} />
        <DataTable testid="tracker-actions-table" rows={rows} columns={[
          ...cols.slice(0, 7),
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "flag", label: "", render: (r) => canEdit && r.status !== "Delayed" && (
            <Button data-testid={`flag-delay-${r.id}`} size="sm" variant="ghost" className="text-rose-600" onClick={() => update(r.id, { status: "Delayed" }, "Flagged as delayed")}><Flag className="h-3.5 w-3.5 mr-1" />Flag delay</Button>
          ) },
        ]} />
      </Panel>
    </>
  );
}
